import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoBack } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api, { SERVER_URL } from '../../utils/api';
import RichTextEditor from '../../components/RichTextEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faPaperclip, faInfoCircle, faLayerGroup, faSpinner, faFileVideo, faFilePdf, faCheckCircle, faRupeeSign, faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import imageCompression from 'browser-image-compression';
import { isImageFile } from '../../utils/mediaTypes';
import UploadProgress from '../../components/UploadProgress';
import '../../styles/App.css';
import '../../styles/expenses.css';

const DEFAULT_LOCATIONS = ['IT Closet', 'Server Room A', 'Storage Cabinet 1'];

const EditInventory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const goBack = useGoBack('/inventory');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isCompressing, setIsCompressing] = useState(false);

    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState(DEFAULT_LOCATIONS);
    // Set when the row is Assigned to a user who has since been deleted — the
    // employee dropdown cannot offer them, so the edit needs a new assignee.
    const [missingAssignee, setMissingAssignee] = useState(false);

    const [formData, setFormData] = useState({
        itemName: '',
        status: 'Available',
        storageLocation: '',
        assignedTo: '',
        notes: '',
        quantityToUpdate: 1,
        price: '' 
    });

    const [originalQuantity, setOriginalQuantity] = useState(1);
    const [originalStatus, setOriginalStatus] = useState('');

    const [existingMedia, setExistingMedia] = useState([]);
    const [newFiles, setNewFiles] = useState({ media: [] });

    const fetchInitialData = useCallback(async () => {
        try {
            // Inactive included, so an asset still assigned to someone who has
            // left shows who has it; the options below offer only active staff.
            const empRes = await api.get('/employees/directory', { params: { includeInactive: true } });
            const employeeArray = Array.isArray(empRes.data) ? empRes.data : (empRes.data?.data || []);
            setEmployees(employeeArray);

            const assetRes = await api.get(`/inventory/${id}`);
            const data = assetRes.data;

            setFormData({
                itemName: data.itemName || '',
                status: data.status || 'Available',
                storageLocation: data.storageLocation || '',
                assignedTo: data.assignedTo?._id || '',
                notes: data.notes || '',
                quantityToUpdate: data.quantity || 1,
                price: data.price !== null && data.price !== undefined ? data.price : '' 
            });

            setOriginalQuantity(data.quantity || 1);
            setOriginalStatus(data.status || 'Available');
            // Deduped because the gallery keys on the URL itself.
            setExistingMedia([...new Set(data.mediaUrls || [])]);

            // The row points at a user, but populate returned nothing — that user
            // was deleted. Without this the assignee select is silently blank and
            // the required field blocks saving with no explanation.
            setMissingAssignee(data.status === 'Assigned' && !data.assignedTo);

            // Dedupe against the previous value, not a stale closure. React 18
            // StrictMode runs this effect twice in dev, which used to append the
            // same location twice and trip the duplicate-key warning.
            if (data.storageLocation) {
                setLocations(prev => (prev.includes(data.storageLocation) ? prev : [...prev, data.storageLocation]));
            }

        } catch (err) {
            Swal.fire('Error', 'Could not load asset details', 'error');
            navigate('/inventory');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        const processedFiles = [];
        const rejected = [];

        setIsCompressing(true);

        for (let file of selectedFiles) {
            // Asset media is photos only. PDFs and videos belong on the expense
            // record, not here — a PDF renders as a broken inventory thumbnail.
            if (!isImageFile(file)) {
                rejected.push(file.name);
                continue;
            }

            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                };

                const compressedBlob = await imageCompression(file, options);

                const safelyNamedFile = new File([compressedBlob], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                });

                processedFiles.push(safelyNamedFile);
            } catch (error) {
                console.error("Error compressing image:", error);
                processedFiles.push(file);
            }
        }

        setNewFiles({ media: processedFiles });
        setIsCompressing(false);

        if (rejected.length > 0) {
            e.target.value = '';
            Swal.fire('Images Only', `Skipped ${rejected.length} file(s) — only images can be attached to an asset:\n${rejected.join(', ')}`, 'warning');
        }
    };

    const removeExistingMedia = (url) => {
        setExistingMedia(prev => prev.filter(u => u !== url));
    };

    const removeNewFile = (index) => {
        setNewFiles(prev => ({ media: prev.media.filter((_, i) => i !== index) }));
    };

    const handleAddLocation = async () => {
        const { value: newLocation } = await Swal.fire({
            title: 'Add New Location',
            input: 'text',
            inputPlaceholder: 'e.g. Warehouse Rack 3',
            showCancelButton: true,
            confirmButtonColor: '#215D7B'
        });
        if (newLocation) {
            const trimmed = newLocation.trim();
            if (!trimmed) return;
            setLocations(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
            setFormData(prev => ({ ...prev, storageLocation: trimmed }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.itemName) return Swal.fire('Error', 'Item Name is required', 'warning');
        if (formData.status === 'Assigned' && !formData.assignedTo) return Swal.fire('Error', 'Please select an employee', 'warning');
        if (formData.quantityToUpdate < 1) return Swal.fire('Error', 'Quantity must be at least 1', 'warning');
        if (formData.quantityToUpdate > originalQuantity) return Swal.fire('Error', `Cannot update more than ${originalQuantity} items`, 'warning');

        setSaving(true);
        setUploadProgress(null);

        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => data.append(key, formData[key]));

            // Anything the user removed is simply absent from this list.
            data.append('keepMediaUrls', JSON.stringify(existingMedia));

            if (newFiles.media && newFiles.media.length > 0) {
                for (let i = 0; i < newFiles.media.length; i++) {
                    data.append('media', newFiles.media[i]);
                }
            }

            await api.put(`/inventory/${id}`, data, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    // No total stays indeterminate rather than showing NaN%.
                    if (!newFiles.media.length || !progressEvent.total) return;
                    setUploadProgress(Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total)));
                }
            });

            Swal.fire({ icon: 'success', title: 'Asset Updated', timer: 1500, showConfirmButton: false });
            goBack();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to update asset', 'error');
        } finally {
            setSaving(false);
            setUploadProgress(null);
        }
    };

    // --- Media Helpers ---
    const getFileUrl = (url) => {
        if (!url) return '';
        return url.startsWith('http') ? url : `${SERVER_URL}${url}`;
    };

    const viewSingleFile = (url, title) => {
        const fullUrl = getFileUrl(url);
        const isVideo = url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
        const isPdf = url.toLowerCase().endsWith('.pdf');

        if (isVideo) {
            Swal.fire({ title, html: `<video src="${fullUrl}" controls style="width:100%; border-radius:6px; max-height:400px; background:#000;"></video>`, width: '800px', showCloseButton: true, showConfirmButton: false });
        } else if (isPdf) {
            Swal.fire({ title, html: `<iframe src="${fullUrl}" width="100%" height="500px" style="border: none; border-radius: 6px;"></iframe>`, width: '800px', showCloseButton: true, showConfirmButton: false });
        } else {
            Swal.fire({ title, imageUrl: fullUrl, imageAlt: title, width: '800px', showCloseButton: true, showConfirmButton: false });
        }
    };

    const removeMediaButton = (url) => (
        <button
            type="button"
            title="Remove this file"
            onClick={(e) => { e.stopPropagation(); removeExistingMedia(url); }}
            style={{
                position: 'absolute', top: '4px', right: '4px', zIndex: 2,
                width: '22px', height: '22px', borderRadius: '50%', border: 'none',
                background: 'rgba(220, 38, 38, 0.92)', color: 'white', cursor: 'pointer',
                fontSize: '11px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
        >
            <FontAwesomeIcon icon={faTimes} />
        </button>
    );

    const renderThumbnail = (url, index) => {
        const fullUrl = getFileUrl(url);
        const isPdf = url.toLowerCase().endsWith('.pdf');
        const isVideo = url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);

        let iconContent;
        if (isPdf) {
            iconContent = <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '32px', color: 'light-dark(#dc2626, var(--theme-danger-ink))' }} />;
        } else if (isVideo) {
            iconContent = <FontAwesomeIcon icon={faFileVideo} style={{ fontSize: '32px', color: 'light-dark(#2563eb, var(--theme-blue-ink, #91b7ff))' }} />;
        } else {
            return (
                <div key={url} className="existing-file-card" style={{ position: 'relative' }}>
                    {removeMediaButton(url)}
                    <img src={fullUrl} alt="Thumbnail" className="existing-file-thumb" onClick={() => viewSingleFile(url, `Media ${index + 1}`)} style={{ cursor: 'pointer' }} />
                </div>
            );
        }

        return (
            <div key={url} className="existing-file-card icon-card" style={{ position: 'relative' }}>
                {removeMediaButton(url)}
                <div onClick={() => viewSingleFile(url, `Media ${index + 1}`)} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {iconContent}
                    <span className="file-type-label">{isPdf ? 'PDF' : 'VIDEO'}</span>
                </div>
            </div>
        );
    };

    if (loading) return <div className="main-content">Loading Asset Data...</div>;

    return (
        <div className="profile-container fade-in">
            <div className="page-header-left">
                <button className="gts-btn warning btn-small m-0" onClick={() => goBack()}>
                    <FontAwesomeIcon icon={faArrowLeft} className="btn-icon" /> Back
                </button>
                <h1 className="page-title header-no-margin">Edit Asset Details</h1>
            </div>

            <div style={{ background: 'light-dark(#eff6ff, var(--theme-accent-soft))', border: '1px solid light-dark(#bfdbfe, var(--theme-border-strong, #4a4e55))', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ background: '#3b82f6', color: 'white', width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                    <FontAwesomeIcon icon={faLayerGroup} />
                </div>
                <div>
                    <h3 style={{ margin: 0, color: 'light-dark(#1e3a8a, var(--theme-blue-ink, #91b7ff))', fontSize: '16px' }}>Editing Stack of {originalQuantity}</h3>
                    <p style={{ margin: 0, color: '#60a5fa', fontSize: '13px' }}>Current Status: {originalStatus}</p>
                </div>
            </div>

            <div className="expense-form-card">
                <form onSubmit={handleSubmit} className="profile-form">

                    {/* --- CORE DETAILS --- */}
                    <div className="expense-form-section">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faInfoCircle} /> Asset Details
                        </div>

                        <div className="expense-grid">

                            <div className="form-group grid-span-2" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                <div style={{ flex: '2', minWidth: '200px' }}>
                                    <label className="input-label">Item / Asset Name *</label>
                                    <input className="custom-input" type="text" name="itemName" required value={formData.itemName} onChange={handleChange} />
                                </div>
                                <div style={{ flex: '1', minWidth: '100px' }}>
                                    <label className="input-label" style={{ color: 'light-dark(#0f172a, var(--theme-ink))', fontWeight: 'bold' }}>Qty to Modify *</label>
                                    <input
                                        className="custom-input"
                                        type="number"
                                        name="quantityToUpdate"
                                        required
                                        min="1"
                                        max={originalQuantity}
                                        value={formData.quantityToUpdate}
                                        onChange={handleChange}
                                        disabled={originalQuantity === 1}
                                        style={{ background: originalQuantity === 1 ? 'light-dark(#f1f5f9, var(--theme-surface-raised, #292c31))' : 'light-dark(#fff, var(--theme-surface, #1a1c1f))' }}
                                    />
                                    {originalQuantity > 1 && (
                                        <div className="text-small text-muted" style={{ marginTop: '4px' }}>Max: {originalQuantity}</div>
                                    )}
                                </div>
                                <div style={{ flex: '1', minWidth: '120px' }}>
                                    <label className="input-label"><FontAwesomeIcon icon={faRupeeSign} className="text-muted mr-5"/> Price (Optional)</label>
                                    <input className="custom-input" type="number" name="price" placeholder="e.g. 500" value={formData.price} onChange={handleChange} />
                                </div>
                            </div>

                            <div className="form-group grid-span-2">
                                <label className="input-label">Target Status for these {formData.quantityToUpdate} item(s)</label>
                                <div className="expense-type-toggle">
                                    {['Available', 'Assigned', 'Damaged', 'Lost'].map(status => (
                                        <label key={status} className={formData.status === status ? 'active' : ''}>
                                            <input type="radio" name="status" value={status} onChange={handleChange} /> {status}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {formData.status === 'Available' && (
                                <div className="form-group grid-span-2">
                                    <label className="input-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Target Storage Location</span>
                                        <span onClick={handleAddLocation} style={{ color: 'light-dark(#215D7B, var(--theme-accent-ink))', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>+ ADD NEW LOCATION</span>
                                    </label>
                                    <select className="swal2-select custom-select" name="storageLocation" required value={formData.storageLocation} onChange={handleChange}>
                                        <option value="">-- Select Location --</option>
                                        {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                    </select>
                                </div>
                            )}

                            {formData.status === 'Assigned' && (
                                <div className="form-group grid-span-2">
                                    <label className="input-label">Assign To Employee *</label>
                                    {missingAssignee && !formData.assignedTo && (
                                        <div style={{ background: 'light-dark(#fffbeb, var(--theme-warning-soft))', border: '1px solid light-dark(#fef3c7, var(--theme-border-strong, #4a4e55))', color: 'light-dark(#b45309, var(--theme-warning-ink))', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', marginBottom: '10px' }}>
                                            <FontAwesomeIcon icon={faTriangleExclamation} /> <strong>The employee this asset was assigned to no longer exists.</strong> Pick a new assignee, or set the status to <em>Available</em> to return it to storage.
                                        </div>
                                    )}
                                    <select className="swal2-select custom-select" name="assignedTo" required value={formData.assignedTo} onChange={handleChange}>
                                        <option value="">-- Select Employee --</option>
                                        {employees
                                            .filter(emp => emp.status !== 'INACTIVE' || emp._id === formData.assignedTo)
                                            .map(emp => (
                                                <option key={emp._id} value={emp._id}>
                                                    {emp.name} ({emp.employeeId || emp.role}){emp.status === 'INACTIVE' ? ' (inactive)' : ''}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            )}

                            <div className="form-group grid-span-2">
                                <label className="input-label">Extra Notes / Serial Numbers</label>
                                <RichTextEditor rows={2} name="notes" value={formData.notes} onChange={(v) => setFormData((f) => ({ ...f, notes: v }))} />
                            </div>
                        </div>
                    </div>

                    <div className="expense-form-section mb-0">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faPaperclip} /> Asset Media
                        </div>

                        <div className="alert-message warning mb-20" style={{ padding: '12px', borderRadius: '8px', fontSize: '13px', background: 'light-dark(#eff6ff, var(--theme-accent-soft))', border: '1px solid light-dark(#bfdbfe, var(--theme-border-strong, #4a4e55))', color: 'light-dark(#1e40af, var(--theme-blue-ink, #91b7ff))' }}>
                            <FontAwesomeIcon icon={faInfoCircle} /> <strong>Note:</strong> Images only. Use the <FontAwesomeIcon icon={faTimes} /> on a file to remove it — removals are saved when you press <strong>Save Updates</strong>.
                        </div>

                        <div className="expense-grid">
                            <div className="form-group expense-file-area grid-span-2">

                                <label className="input-label" style={{ borderBottom: '1px solid light-dark(#e2e8f0, var(--theme-border, #33363b))', paddingBottom: '10px', marginBottom: '15px' }}>
                                    Photos of Asset
                                </label>

                                {existingMedia.length > 0 ? (
                                    <div className="mb-15">
                                        <div className="text-small text-muted mb-10 fw-600">Currently Saved Files:</div>
                                        <div className="existing-file-gallery">
                                            {existingMedia.map((url, idx) => renderThumbnail(url, idx))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-small text-muted mb-15">No photos saved for this asset yet.</div>
                                )}

                                <div className="upload-container-new">
                                    <div className="text-small text-muted mb-5 fw-600">Upload Additional Images (Optional):</div>
                                    <input className="custom-file-input" type="file" multiple accept="image/*" onChange={handleFileChange} />

                                    {isCompressing ? (
                                        <div className="file-success-badge mt-10" style={{ background: 'light-dark(#fef3c7, var(--theme-warning-soft))', color: 'light-dark(#b45309, var(--theme-warning-ink))' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Compressing...
                                        </div>
                                    ) : newFiles.media.length > 0 && (
                                        <div className="mt-10">
                                            <div className="file-success-badge">
                                                <FontAwesomeIcon icon={faCheckCircle} /> {newFiles.media.length} new image(s) ready
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                                {newFiles.media.map((file, idx) => (
                                                    <span key={`${file.name}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'light-dark(#f1f5f9, var(--theme-surface-raised))', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', color: 'light-dark(#475569, var(--theme-body))' }}>
                                                        {file.name}
                                                        <button type="button" title="Remove" onClick={() => removeNewFile(idx)} style={{ background: 'none', border: 'none', color: 'light-dark(#dc2626, var(--theme-danger-ink))', cursor: 'pointer', padding: 0 }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-actions mt-30 flex-col gap-15">
                        {saving && (
                            <UploadProgress
                                progress={uploadProgress}
                                count={newFiles.media.length}
                                savingLabel={newFiles.media.length ? 'Saving to storage…' : 'Saving changes…'}
                            />
                        )}

                        <button type="submit" className="save-btn expense-submit-btn" disabled={saving || isCompressing}>
                            <FontAwesomeIcon icon={faSave} className="btn-icon" />
                            {saving ? 'Processing...' : isCompressing ? 'Compressing Files...' : 'Save Updates'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default EditInventory;